import * as React from "react";
import {
  Form,
  Link,
  redirect,
  useLoaderData,
  useNavigate,
  useNavigation,
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
} from "react-router";
import { ArrowLeft, Ban, Circle, Loader2, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import ColoredWord from "@/components/ColoredWord";
import { api } from "@/lib/api";
import { invalidatePage } from "@/lib/pages";
import { cn } from "@/lib/utils";
import { useColorsSettings } from "@/store/settings";
import { COLORS_TRANSLATION, TEXT_COLORS } from "@/utils";
import { INPUT_CLASS } from "@/pages/Login";

import { WORD_COLORS, WORD_SHAPES, type Word } from "@/lib/types";

export async function wordEditorLoader({ params, request }: LoaderFunctionArgs) {
  const pageNumber = Number(params.pageNumber);
  const wordId = new URL(request.url).searchParams.get("word");
  if (!wordId) return { pageNumber, word: null };

  const word = await api<Word>(`/api/pages/${pageNumber}/words/${wordId}`);
  return { pageNumber, word };
}

export async function wordEditorAction({ params, request }: ActionFunctionArgs) {
  const pageNumber = Number(params.pageNumber);
  const values = Object.fromEntries(await request.formData());

  await api(`/api/pages/${pageNumber}/words/${values.id}`, {
    method: "PATCH",
    body: JSON.stringify({
      color: values.color,
      shape: values.shape,
      translation_bs: values.translation_bs,
    }),
  });
  invalidatePage(pageNumber);
  return redirect(`/${pageNumber}`);
}

const SHAPE_OPTIONS: Record<(typeof WORD_SHAPES)[number], { label: string; icon: React.ReactNode }> = {
  SQUARE: {
    label: "Kvadrat",
    icon: <Square className="fill-ink-subtle/35 text-ink-subtle" />,
  },
  "SQUARE-BORDER-ONLY": {
    label: "Okvir kvadrata",
    icon: <Square className="text-ink-subtle" />,
  },
  CIRCLE: {
    label: "Krug",
    icon: <Circle className="fill-ink-subtle/35 text-ink-subtle" />,
  },
  "CIRCLE-BORDER-ONLY": {
    label: "Okvir kruga",
    icon: <Circle className="text-ink-subtle" />,
  },
};

const FIELD_LABEL = "text-sm font-medium text-ink-muted";
const ROW = "flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4";

/** Plain left clicks close in place (animated); modified clicks keep the link's default. */
function isPlainClick(e: React.MouseEvent) {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

const WordEditor: React.FC = () => {
  const { word, pageNumber } = useLoaderData<Awaited<ReturnType<typeof wordEditorLoader>>>();
  const navigate = useNavigate();
  const linkBackwards = `/${pageNumber}`;
  const contentRef = React.useRef<HTMLDivElement>(null);

  // The editor is a route: closing plays the dialog's exit, and only once the
  // content has unmounted do we navigate back to the page. A save redirects
  // instead, so `closing` stays false and nothing navigates twice.
  const [open, setOpen] = React.useState(true);
  const closing = React.useRef(false);
  // The word link that opened the editor, to hand focus back on close.
  const [returnFocus] = React.useState(() =>
    typeof document === "undefined" ? null : (document.activeElement as HTMLElement | null)
  );

  const close = React.useCallback(() => {
    closing.current = true;
    setOpen(false);
  }, []);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent
        ref={contentRef}
        aria-describedby={undefined}
        className="flex max-h-[calc(100svh-2rem)] max-w-md flex-col gap-0 p-0"
        // Land on the dialog itself (the title is announced); Tab then reaches "Nazad".
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          contentRef.current?.focus();
        }}
        onCloseAutoFocus={(e) => {
          if (!closing.current) return;
          e.preventDefault();
          if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
          navigate(linkBackwards, { preventScrollReset: true });
        }}
      >
        {/* Three equal-sided columns keep the title centred; the primitive's
            close button (absolute, right-3 top-3) sits over the empty right one. */}
        <div className="grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-2 border-b border-paper-300/70 px-3 py-2.5">
          <Link
            to={linkBackwards}
            preventScrollReset
            onClick={(e) => {
              if (!isPlainClick(e)) return;
              e.preventDefault();
              close();
            }}
            className="inline-flex h-10 items-center gap-1.5 justify-self-start rounded-control px-2 text-sm font-medium text-ink-muted transition-colors duration-150 hover:text-ink"
          >
            <ArrowLeft aria-hidden className="size-4" />
            Nazad
          </Link>
          <DialogTitle className="text-[15px]">Uredi riječ</DialogTitle>
        </div>

        {!word ? (
          <div className="p-5">
            <p className="rounded-card bg-paper-100 px-4 py-6 text-center text-sm text-ink-muted ring-1 ring-paper-300">
              Nijedna riječ nije odabrana za uređivanje.
            </p>
          </div>
        ) : (
          <WordForm key={word.id} word={word} pageNumber={pageNumber} />
        )}
      </DialogContent>
    </Dialog>
  );
};

function WordForm({ word, pageNumber }: { word: Word; pageNumber: number }) {
  const navigation = useNavigation();
  // Submitting, then following the action's redirect back to the page.
  const saving = navigation.state !== "idle" && navigation.formData != null;
  const { colors } = useColorsSettings();
  const [selectedColor, setSelectedColor] = React.useState(word.color ?? "TRANSPARENT");
  const [selectedShape, setSelectedShape] = React.useState(word.shape ?? "SQUARE");
  const ids = React.useId();

  return (
    <Form
      action={`/${pageNumber}/edit?word=${word.id}`}
      method="post"
      preventScrollReset
      className="flex min-h-0 flex-1 flex-col"
    >
      <input type="hidden" value={word.id} name="id" />
      <input type="hidden" value={selectedColor} name="color" />
      <input type="hidden" value={selectedShape} name="shape" />

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-6">
        {/* Live specimen: the word in its page font with the chosen colour and shape. */}
        <div
          aria-hidden
          className="mx-5 mt-5 grid place-items-center overflow-hidden rounded-xl bg-paper-100 px-4 py-6 text-ink ring-1 ring-paper-300"
          style={{ fontFamily: `p${pageNumber}` }}
        >
          <ColoredWord
            word={{ ...word, color: selectedColor, shape: selectedShape }}
            className="text-5xl leading-[1.4] sm:text-6xl"
          />
        </div>
        {!colors && (
          <p className="mx-5 mt-2 text-center text-xs text-ink-subtle">
            Poveznice su isključene u podešenjima, pa se boja ne vidi u pregledu.
          </p>
        )}

        <div className={cn(ROW, "px-5 pt-6")}>
          <span id={`${ids}-color`} className={FIELD_LABEL}>
            Boja
          </span>
          <ToggleGroup
            type="single"
            variant="outline"
            aria-labelledby={`${ids}-color`}
            value={selectedColor}
            onValueChange={(value) => value && setSelectedColor(value)}
          >
            {WORD_COLORS.map((color) => {
              const label =
                color === "TRANSPARENT" ? "Bez boje" : COLORS_TRANSLATION.get(color) ?? color;
              return (
                <ToggleGroupItem key={color} value={color} aria-label={label} title={label}>
                  {color === "TRANSPARENT" ? (
                    <Ban aria-hidden className="text-ink-subtle" />
                  ) : (
                    <span
                      aria-hidden
                      className="size-5 rounded-[4px] ring-1 ring-ink/10"
                      style={{ background: TEXT_COLORS.get(color) }}
                    />
                  )}
                </ToggleGroupItem>
              );
            })}
          </ToggleGroup>
        </div>

        <div className={cn(ROW, "px-5 pt-5")}>
          <span id={`${ids}-shape`} className={FIELD_LABEL}>
            Oblik
          </span>
          <ToggleGroup
            type="single"
            variant="outline"
            aria-labelledby={`${ids}-shape`}
            value={selectedShape}
            onValueChange={(value) => value && setSelectedShape(value)}
          >
            {WORD_SHAPES.map((shape) => (
              <ToggleGroupItem
                key={shape}
                value={shape}
                aria-label={SHAPE_OPTIONS[shape].label}
                title={SHAPE_OPTIONS[shape].label}
              >
                {SHAPE_OPTIONS[shape].icon}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <div className="space-y-4 px-5 pt-6">
          <div>
            <label htmlFor={`${ids}-bs`} className={FIELD_LABEL}>
              Prijevod - Bosanski
            </label>
            <input
              id={`${ids}-bs`}
              type="text"
              name="translation_bs"
              autoComplete="off"
              className={cn(INPUT_CLASS, "mt-1.5")}
              defaultValue={word.translation_bs ?? ""}
            />
          </div>
          <div>
            <label htmlFor={`${ids}-en`} className={FIELD_LABEL}>
              Prijevod - Engleski
            </label>
            <input
              id={`${ids}-en`}
              type="text"
              name="translation_en"
              disabled
              className={cn(
                INPUT_CLASS,
                "mt-1.5 disabled:cursor-not-allowed disabled:bg-paper-200 disabled:text-ink-subtle"
              )}
              defaultValue={word.translation_en}
            />
          </div>
        </div>
      </div>

      <div className="shrink-0 border-t border-paper-300/70 bg-paper-50/95 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <Button type="submit" size="lg" disabled={saving} className="w-full disabled:opacity-80">
          {saving && <Loader2 aria-hidden className="animate-spin" />}
          {saving ? "Spremanje..." : "Spremi izmjene"}
        </Button>
      </div>
    </Form>
  );
}

export default WordEditor;
